import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, Loader2, HelpCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Map of known app routes. `labels` is checked case-insensitively against bot
// responses to decide when to show a "Take me there" button. `page` is the
// AuthContext accessMap key used to hide routes the employee can't access
// (no `page` = accessible to everyone, e.g. change-password).
const APP_ROUTES = [
  { path: '/', label: 'Dashboard', page: 'dashboard', aliases: ['dashboard', 'home'] },
  { path: '/analytics', label: 'Analytics', page: 'analytics', aliases: ['analytics', 'sales analytics'] },
  { path: '/sales-pipeline', label: 'Sales Pipeline', page: 'sales-pipeline', aliases: ['sales pipeline', 'pipeline'] },
  { path: '/retail-worksheet', label: 'Retail Worksheet', page: 'retail-worksheet', aliases: ['retail worksheet', 'worksheet'] },
  { path: '/brokered-deals', label: 'Brokered Deals', page: 'brokered-deals', aliases: ['brokered deals', 'broker'] },
  { path: '/calculator', label: 'Calculator', page: 'calculator', aliases: ['calculator', 'payment calculator'] },
  { path: '/inventory', label: 'Inventory', page: 'inventory', aliases: ['inventory', 'vehicles'] },
  { path: '/delivery', label: 'Deliveries', page: 'delivery', aliases: ['deliveries', 'delivery'] },
  { path: '/trade-in-estimator', label: 'Appraisals', page: 'trade-in-estimator', aliases: ['appraisals', 'trade appraisals', 'trade-in', 'trade in', 'trade in estimator'] },
  { path: '/customers', label: 'Customers', page: 'customers', aliases: ['customers'] },
  { path: '/customer-messages', label: 'Customer Messages', page: 'customer-messages', aliases: ['customer messages', 'messages'] },
  { path: '/customer-portal-management', label: 'Customer Portal', page: 'customer-portal-management', aliases: ['customer portal', 'portal management'] },
  { path: '/email-campaigns', label: 'Email Campaigns', page: 'email-campaigns', aliases: ['email campaigns', 'campaigns'] },
  { path: '/sales-staff', label: 'Sales Staff', page: 'sales-staff', aliases: ['sales staff', 'staff'] },
  { path: '/accounting', label: 'Accounting', page: 'accounting', aliases: ['accounting', 'financials'] },
  { path: '/promotions', label: 'Promotions', page: 'promotions', aliases: ['promotions'] },
  { path: '/admin-panel', label: 'Admin Panel', page: 'admin-panel', aliases: ['admin panel', 'admin'] },
  { path: '/change-password', label: 'Change Password', aliases: ['change password'] },
];

function detectRouteInText(text) {
  if (!text) return null;
  const t = text.toLowerCase();
  // Prefer longer aliases so "sales pipeline" beats "sales"
  const candidates = APP_ROUTES.flatMap(r => r.aliases.map(a => ({ route: r, alias: a })))
    .sort((a, b) => b.alias.length - a.alias.length);
  for (const { route, alias } of candidates) {
    const idx = t.indexOf(alias);
    if (idx < 0) continue;
    const before = t[idx - 1] || ' ';
    const after = t[idx + alias.length] || ' ';
    if (/[a-z0-9]/.test(before) || /[a-z0-9]/.test(after)) continue;
    return route;
  }
  return null;
}

export default function HelpBot({ onClose }) {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  // Only offer navigation to pages the current employee can actually open.
  const canGo = (route) => !!route && (!route.page || hasAccess(route.page));
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const newSessionId = `chat_${Date.now()}`;
    setSessionId(newSessionId);

    setChatHistory([{
      role: 'assistant',
      message:
        "Hi! I'm your Modern-Motors guide. Ask me how to do anything in the app — find a deal, add inventory, edit the monthly goal, run analytics, decode a VIN, and more. When I mention a page, tap \"Take me there\" to jump straight to it.",
      timestamp: new Date().toISOString(),
    }]);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getCurrentPageContext = () => {
    const path = window.location.pathname;
    const match = APP_ROUTES.find(r => r.path === path);
    return {
      page: match?.label || path,
      path,
    };
  };

  const handleTakeMeThere = (path) => {
    navigate(path);
    if (typeof onClose === 'function') onClose();
  };

  const handleSendMessage = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage('');
    setLoading(true);

    const newUserMessage = {
      role: 'user',
      message: userMessage,
      timestamp: new Date().toISOString(),
    };
    setChatHistory(prev => [...prev, newUserMessage]);

    const streamingMessageId = Date.now();
    const streamingMessage = {
      id: streamingMessageId,
      role: 'assistant',
      message: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };
    setChatHistory(prev => [...prev, streamingMessage]);

    try {
      const token = localStorage.getItem('employee_token');
      const context = getCurrentPageContext();

      const response = await fetch(`${API}/helpbot/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          context,
          session_id: sessionId,
        }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'content') {
                fullResponse += data.content;
                setChatHistory(prev =>
                  prev.map(msg =>
                    msg.id === streamingMessageId ? { ...msg, message: fullResponse } : msg
                  )
                );
              } else if (data.type === 'done') {
                const route = detectRouteInText(fullResponse);
                setChatHistory(prev =>
                  prev.map(msg =>
                    msg.id === streamingMessageId
                      ? { ...msg, isStreaming: false, route: route || null }
                      : msg
                  )
                );
              } else if (data.type === 'error') {
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming chat error:', error);
      let errorMsg = 'Failed to send message. Please try again.';
      if (error.message.includes('401')) {
        errorMsg = 'Authentication failed. Please try logging out and logging back in.';
      } else if (error.message.includes('500')) {
        errorMsg = 'Server error. The AI service may be temporarily unavailable.';
      }
      toast.error(errorMsg);

      setChatHistory(prev =>
        prev.map(msg =>
          msg.id === streamingMessageId
            ? {
                ...msg,
                message: "I'm having trouble responding right now. " + errorMsg,
                isError: true,
                isStreaming: false,
              }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const quickLinks = APP_ROUTES.filter(canGo).slice(0, 6);

  return (
    <div className="fixed right-0 md:right-4 top-0 md:top-20 bottom-0 md:bottom-20 w-full md:w-96 bg-card md:rounded-xl shadow-2xl border-l md:border border-border flex flex-col z-50">
      {/* Header */}
      <div className="bg-ink-950 dark:bg-ink-900 border-b border-gold-500/40 p-4 md:rounded-t-xl flex items-center justify-between">
        <div className="flex items-center gap-3 text-white">
          <div className="relative">
            <Bot className="w-6 h-6 text-gold-400" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-[15px]">AI Help Assistant</h3>
            <p className="text-[11px] text-ink-300">App guide</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          title="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-ink-50 dark:bg-ink-950">
        {chatHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-ink-400">
            <HelpCircle className="w-12 h-12 mb-2" />
            <p className="text-sm text-center">Ask me anything about the app!</p>
          </div>
        ) : (
          chatHistory.map((msg, idx) => {
            const detected = msg.role === 'assistant' && !msg.isStreaming
              ? (msg.route || detectRouteInText(msg.message))
              : null;
            return (
              <div
                key={msg.id || idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-ink-950 text-white dark:bg-gold-500 dark:text-ink-950'
                      : msg.isError
                      ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
                      : 'bg-card text-foreground shadow-sm border border-border'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {msg.message}
                    {msg.isStreaming && <span className="inline-block w-2 h-4 bg-gold-500 ml-1 animate-pulse"></span>}
                  </p>
                  {detected && canGo(detected) && (
                    <button
                      onClick={() => handleTakeMeThere(detected.path)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-ink-950 text-white dark:bg-gold-500 dark:text-ink-950 hover:opacity-90 px-3 py-1.5 rounded-full transition-colors"
                    >
                      Take me to {detected.label}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <p
                    className={`text-xs mt-2 ${
                      msg.role === 'user' ? 'text-white/70 dark:text-ink-950/70' : 'text-ink-400'
                    }`}
                  >
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            );
          })
        )}

        {/* Quick links after welcome */}
        {chatHistory.length === 1 && chatHistory[0].role === 'assistant' && (
          <div className="flex flex-wrap gap-1.5">
            {quickLinks.map(r => (
              <button
                key={r.path}
                onClick={() => handleTakeMeThere(r.path)}
                className="text-[11px] px-2.5 py-1 rounded-full bg-card border border-border text-ink-600 dark:text-ink-300 hover:border-gold-500 hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
              >
                {r.label}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-card text-foreground rounded-2xl px-4 py-3 shadow-sm border border-border">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-gold-500" />
                <span className="text-sm text-ink-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-border bg-card md:rounded-b-xl">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Ask me anything..."
            className="flex-1 px-4 py-3 border border-border bg-card text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
            disabled={loading}
          />
          <button
            onClick={handleSendMessage}
            disabled={loading || !message.trim()}
            className="bg-ink-950 text-white dark:bg-gold-500 dark:text-ink-950 p-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Send message"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-ink-500">Enter to send • ESC to close</p>
          <div className="flex items-center gap-1 text-xs text-ink-400">
            <span>AI-powered</span>
          </div>
        </div>
      </div>
    </div>
  );
}
